import { CellContext, ColumnDef } from '@tanstack/react-table';
import { DateTime, Interval } from 'luxon';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import Button from '../../../../../components/Button/Button';
import Card from '../../../../../components/Card/Card';
import Dropdown from '../../../../../components/Dropdown/Dropdown';
import UnsavedChangesModal from '../../../../../components/Modal/UnsavedChangesModal/UnsavedChangesModal';
import Radio from '../../../../../components/Radio/Radio';
import Table from '../../../../../components/Tables/Table/Table';
import { showToast } from '../../../../../components/Toast/Toast';
import { ToastTypes } from '../../../../../components/Toast/Toast.types';
import Tooltip from '../../../../../components/Tooltip/Tooltip';
import { PositionTypes } from '../../../../../components/Tooltip/Tooltip.types';
import { useEditNationalProgramPriceSelection } from '../../../../../services/bundles/bundles.service';
import { StoreGroup } from '../../../../../types/bundles.types';
import {
  NationalProgramGroup,
  NationalProgramResponse as NationalProgramType,
  NationalProgramStates,
} from '../../../../../types/nationalProgram.types';
import useConfirmationModal from '../../../../../utils/useConfirmationModal/useConfirmationModal';
import styles from './NationalProgram.module.css';

interface NationalProgramProps extends NationalProgramType {
  bundleGroups: StoreGroup[];
  refetch: () => void;
  isViewOnly: boolean;
  redeemableOnline?: boolean;
  visibleOnline?: boolean;
}

const NationalProgram: FC<NationalProgramProps> = ({
  state,
  nationalProgram,
  chosenPrices,
  bundleGroups,
  refetch,
  isViewOnly,
  redeemableOnline,
  visibleOnline,
}) => {
  const { optInStartDate, freezeStartDate, freezeEndDate, nationalPricePoints, nationalDefaultPrice, optOutHidden } =
    nationalProgram!;
  const franchiseeSelections = chosenPrices!.reduce(
    (acc: { [groupId: string]: number | 'optOut' }, val: NationalProgramGroup) => {
      const price = val.chosenPrice === null ? 'optOut' : val.chosenPrice;
      acc[val.groupId] = price;
      return acc;
    },
    {},
  );
  const [selectedPrices, setSelectedPrices] = useState<{ [groupId: string]: number | 'optOut' }>(franchiseeSelections);
  const [unsavedChangesExist, setUnsavedChangesExist] = useState(false);

  const { open, setHideStatusUpdateText, setTitle } = useConfirmationModal();

  const { mutate, isLoading } = useEditNationalProgramPriceSelection(nationalProgram!.id, nationalProgram!.bundleId);
  const handleSaveClick = () => {
    mutate(
      {
        bundleId: nationalProgram!.bundleId,
        groups: bundleGroups.map((group) => {
          const chosenPrice = selectedPrices[group.groupId] || nationalDefaultPrice;
          return {
            groupId: group.groupId,
            chosenPrice: chosenPrice === 'optOut' ? null : chosenPrice.toFixed(2),
          };
        }),
      },
      {
        onSuccess: () => {
          refetch();
          setUnsavedChangesExist(false);
          setHideStatusUpdateText(true);
          open();
          setTitle('Your request has been sent!');
        },
        onError: () => {
          showToast({
            toastType: ToastTypes.Error,
            label: 'Error saving national program prices',
            id: 'npPriceSelectError',
          });
        },
      },
    );
  };

  useEffect(() => {
    // check for unsaved changes
    let unsavedExists = false;
    bundleGroups.forEach((group) => {
      // check against franchiseeSelections from API to see if any have changed
      if (
        franchiseeSelections[group.groupId] !== undefined &&
        franchiseeSelections[group.groupId] !== selectedPrices[group.groupId]
      ) {
        unsavedExists = true;
      }
      // if not in results from the API && not currently set to defaultPrice, then there is an unsaved change
      if (
        franchiseeSelections[group.groupId] === undefined &&
        selectedPrices[group.groupId] !== undefined &&
        selectedPrices[group.groupId] !== nationalDefaultPrice
      ) {
        unsavedExists = true;
      }
    });
    setUnsavedChangesExist(unsavedExists);
  }, [franchiseeSelections, selectedPrices, bundleGroups, nationalDefaultPrice]);

  const tableData = useMemo(
    () =>
      bundleGroups.map((group) => {
        const chosenPrice = selectedPrices[group.groupId] || nationalDefaultPrice;
        return {
          groupName: group.groupName,
          groupId: group.groupId,
          chosenPrice: chosenPrice === 'optOut' ? 'optOut' : chosenPrice.toFixed(2),
          priceOptions: Object.values([...nationalPricePoints, nationalDefaultPrice, 'optOut']).reduce(
            (acc: { [groupId: string]: boolean }, val: string | number, currentIndex, array) => {
              // for this group, save true or false for each price based on if the price is included in franchisee selections from API
              acc[val] = selectedPrices[group.groupId] === val;
              // if API does not have a selected price for this group, set true for the default price (which is the last val in the list)
              if (selectedPrices[group.groupId] === undefined && currentIndex === array.length - 1) acc[val] = true;
              return acc;
            },
            {},
          ),
        };
      }),
    [bundleGroups, selectedPrices, nationalDefaultPrice, nationalPricePoints],
  );

  const RadioCell = useCallback(
    (
      props: CellContext<{ groupName: string; groupId: string; priceOptions: { [price: string]: boolean } }, unknown>,
    ) => (
      <Radio
        id={`${props.row.original.groupName}${props.column.id}`}
        name={props.row.original.groupId}
        label={props.column.id === 'optOut' ? 'Opt Out' : `$${parseFloat(props.column.id).toFixed(2)}`}
        onChange={() => {
          setSelectedPrices((prevSelectedPrices) => {
            const newPrice = props.column.id === 'optOut' ? 'optOut' : +props.column.id;
            return {
              ...prevSelectedPrices,
              [props.row.original.groupId]: newPrice,
            };
          });
        }}
        checked={props.row.original.priceOptions[props.column.id]}
        disabled={isViewOnly}
      />
    ),
    [isViewOnly],
  );

  const DropdownCell = useCallback(
    (
      props: CellContext<{ groupName: string; groupId: string; priceOptions: { [price: string]: boolean } }, unknown>,
    ) => (
      <Dropdown
        className={styles.dropdownCell}
        label=""
        id="selectNationalProgramPrice"
        value={Object.keys(props.row.original.priceOptions).find((option) => props.row.original.priceOptions[option])}
        options={Object.keys(props.row.original.priceOptions).map((option) => ({
          label: option === 'optOut' ? 'Opt Out' : `$${Number(option).toFixed(2)}`,
          value: option,
        }))}
        disabled={isViewOnly}
        onChange={(event) => {
          setSelectedPrices((prevSelectedPrices) => {
            const newPrice = event.target.value === 'optOut' ? 'optOut' : +event.target.value;
            return {
              ...prevSelectedPrices,
              [props.row.original.groupId]: newPrice,
            };
          });
        }}
        aria-label={`select national program price for ${props.row.original.groupName}`}
      />
    ),
    [isViewOnly],
  );

  const OptOutHeader = useCallback(
    () => (
      <div className={styles.optOutHeader}>
        Opt Out
        <Tooltip
          content="Bundle groups that opt out of a National Program will not be able to offer the bundle for the duration of the program."
          position={PositionTypes.Top}
        />
      </div>
    ),
    [],
  );

  // dynamically generate columns for table based on number of price options
  const columnsForPriceOptions = nationalPricePoints?.filter(Boolean).map((price, index) => ({
    id: price.toString(),
    cell: RadioCell,
    header: `Price Point ${index + 2}`,
    accessorKey: '',
  }));

  // add column for opt out if optOutHidden is false
  const optOutColumn = {
    header: OptOutHeader,
    id: 'optOut',
    accessorKey: '',
    cell: RadioCell,
  };

  const isOptIn = state === NationalProgramStates.OPT_IN;

  const columns = [
    { accessorKey: 'groupName', header: 'Store Group' },
    {
      id: nationalDefaultPrice.toString(),
      cell: RadioCell,
      header: `National Price`,
      accessorKey: '',
    },
    ...(columnsForPriceOptions.length > 0 ? columnsForPriceOptions : []),
    ...(optOutHidden ? [] : [optOutColumn]),
  ] as ColumnDef<{
    groupName: string;
    groupId: string;
    chosenPrice: string;
    priceOptions: { [groupId: string]: boolean };
  }>[];

  const columnsWithDropdown = [
    { accessorKey: 'groupName', header: 'Store Group' },
    {
      id: nationalDefaultPrice.toString(),
      cell: DropdownCell,
      header: `Price Point`,
    },
  ] as ColumnDef<{
    groupName: string;
    groupId: string;
    chosenPrice: string;
    priceOptions: { [groupId: string]: boolean };
  }>[];

  const countDownMessage = isOptIn
    ? `${Interval.fromDateTimes(DateTime.local(), DateTime.fromISO(freezeStartDate)).count('days')} Days Remaining`
    : `LIVE`;

  return (
    <div className={styles.container} data-testid={`nationalProgram${state.replace('_', '').toLowerCase()}`}>
      <h2 className={styles.header}>
        National Program - <span>{state.replace('_', '-').toLowerCase()}</span>
      </h2>
      <Card className={styles.card}>
        <div className={styles.countdown}>{countDownMessage}</div>
        <h3>Program Details</h3>
        <div className={styles.programDetails}>
          <div className={styles.dateColumn}>
            <div className={styles.dateRow}>
              <span className={styles.dateLabel}>Opt-In Date:</span>
              <span className={styles.textLabel}>
                {DateTime.fromISO(optInStartDate, { setZone: true }).toFormat('D')}
              </span>
            </div>
          </div>
          <div className={styles.dateColumn}>
            <div className={styles.dateRow}>
              <span className={styles.dateLabel}>Start Date:</span>
              <span className={styles.textLabel}>
                {DateTime.fromISO(freezeStartDate, { setZone: true }).toFormat('D')}
              </span>
            </div>
            <div className={styles.dateRow}>
              <span className={styles.dateLabel}>End Date:</span>
              <span className={styles.eDLabel}>{DateTime.fromISO(freezeEndDate, { setZone: true }).toFormat('D')}</span>
            </div>
          </div>
          <div className={styles.dateColumn}>
            <div className={styles.dateRow}>
              <span className={styles.dateLabel}>Redeem Online:</span>
              <span data-testid="redeemableOnlineValue" className={styles.textLabel}>
                {redeemableOnline ? 'Yes' : 'No'}
              </span>
            </div>
            <div className={styles.dateRow}>
              <span className={styles.dateLabel}>Visible Online:</span>
              <span className={styles.vOLabel}>{visibleOnline ? 'Yes' : 'No'}</span>
            </div>
          </div>
        </div>
        <div className={styles.pricePointSelection}>
          <h3>Price Point Selection</h3>
          {isOptIn && (
            <p>If you have questions about the available price points please contact your brand representative.</p>
          )}
          {isOptIn ? (
            // opt-in has both a mobile version and a desktop version because the radio options are replaced by a dropdown on mobile
            <>
              <Table data={tableData} columns={columns} includeMinWidth={false} className={styles.desktopTable} />
              <Table
                data={tableData}
                columns={columnsWithDropdown}
                includeMinWidth={false}
                className={styles.mobileTable}
              />
            </>
          ) : (
            <Table
              includeMinWidth={false}
              data={tableData}
              columns={[
                { accessorKey: 'groupName', header: 'Store Group' },
                {
                  id: 'chosenPrice',
                  accessorFn: (row) => (row.chosenPrice === 'optOut' ? 'Opt Out' : `$${row.chosenPrice}`),
                  header: 'Selected Price',
                },
              ]}
            />
          )}
          {isOptIn && !isViewOnly && (
            <div className={styles.saveButton}>
              <Button type="button" onClick={handleSaveClick} disabled={!unsavedChangesExist} isLoading={isLoading}>
                Save
              </Button>
            </div>
          )}
        </div>
      </Card>
      <UnsavedChangesModal unsavedChangesExist={unsavedChangesExist} />
    </div>
  );
};

export default NationalProgram;
